const SEARCH_TRACKING_APP_VISITOR_COOKIE = "_unbound_vid";
const SEARCH_TRACKING_FALLBACK_VISITOR_COOKIE = "unbound_search_visitor_id";
const SEARCH_TRACKING_SESSION_KEY = "unbound_search_session_id";
const SEARCH_TRACKING_ENTRY_KEY = "unbound_search_entry_context";
const SEARCH_TRACKING_ATTRIBUTED_PRODUCTS_KEY = "unbound_search_attributed_products";
const SEARCH_TRACKING_PURCHASED_ORDERS_KEY = "unbound_search_purchased_orders";
const SEARCH_TRACKING_ENTRY_TTL_MS = 30 * 60 * 1000;
const SEARCH_TRACKING_ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SEARCH_TRACKING_DEDUPE_WINDOW_MS = 2500;
const recentSearchTrackingEvents = new Map();
let cachedSearchTrackingClient = undefined;

function getSearchTrackingConfig() {
  return window.UNBOUND_SEARCH_TRACKING_CONFIG || {};
}

function getSearchTrackingClient() {
  const apiClient = window.UnboundSearchApi?.getClient?.();
  if (apiClient) return apiClient;

  if (cachedSearchTrackingClient !== undefined) {
    return cachedSearchTrackingClient;
  }

  const helperFactory = window.UnboundSearchThemeClient?.createSearchClient;
  const config = window.UNBOUND_SEARCH_CLIENT_CONFIG || {};

  if (
    typeof helperFactory !== "function" ||
    !sanitizeSearchTrackingString(config.apiBaseUrl) ||
    !sanitizeSearchTrackingString(config.shop)
  ) {
    cachedSearchTrackingClient = null;
    return cachedSearchTrackingClient;
  }

  try {
    cachedSearchTrackingClient = helperFactory(config);
  } catch {
    cachedSearchTrackingClient = null;
  }

  return cachedSearchTrackingClient;
}

function normalizeSearchTrackingValue(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function sanitizeSearchTrackingString(value) {
  const normalized = normalizeSearchTrackingValue(value);
  return normalized || "";
}

function normalizeSearchTrackingMarketId(value) {
  const normalized = sanitizeSearchTrackingString(value).toLowerCase();

  if (!normalized) return "";
  if (normalized === "us" || normalized === "ca" || normalized === "intl") return normalized;

  return "intl";
}

function sanitizeSearchTrackingInteger(value) {
  const normalized = Number.parseInt(value, 10);
  return Number.isFinite(normalized) ? normalized : null;
}

function sanitizeSearchTrackingNumber(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function normalizeSearchTrackingProductId(value) {
  const normalized = sanitizeSearchTrackingString(value);
  if (!normalized) return "";
  if (normalized.startsWith("gid://")) return normalized;

  const numericMatch = normalized.match(/\d+/)?.[0];
  return numericMatch ? `gid://shopify/Product/${numericMatch}` : normalized;
}

function normalizeSearchTrackingGender(value) {
  const normalized = sanitizeSearchTrackingString(value).toLowerCase();

  if (!normalized) return "";
  if (normalized === "men" || normalized === "mens") return "mens";
  if (normalized === "women" || normalized === "womens") return "womens";

  return "";
}

function createSearchTrackingUuid() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const randomValue = Math.floor(Math.random() * 16);
    const computedValue = character === "x" ? randomValue : (randomValue & 0x3) | 0x8;
    return computedValue.toString(16);
  });
}

function readSearchTrackingCookie(name) {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escapedName}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : "";
}

function writeSearchTrackingCookie(name, value, maxAgeSeconds) {
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    "Path=/",
    "SameSite=Lax",
    `Max-Age=${maxAgeSeconds}`,
  ].join("; ");
}

function getSearchTrackingVisitorId() {
  if (getSearchTrackingClient()) {
    return readSearchTrackingCookie(SEARCH_TRACKING_APP_VISITOR_COOKIE);
  }

  const existingVisitorId =
    readSearchTrackingCookie(SEARCH_TRACKING_APP_VISITOR_COOKIE) ||
    readSearchTrackingCookie(SEARCH_TRACKING_FALLBACK_VISITOR_COOKIE);
  if (existingVisitorId) return existingVisitorId;

  const visitorId = createSearchTrackingUuid();
  writeSearchTrackingCookie(SEARCH_TRACKING_FALLBACK_VISITOR_COOKIE, visitorId, 60 * 60 * 24 * 365);
  return visitorId;
}

function getSearchTrackingSessionId() {
  try {
    const existingSessionId = window.sessionStorage.getItem(SEARCH_TRACKING_SESSION_KEY);
    if (existingSessionId) return existingSessionId;

    const sessionId = createSearchTrackingUuid();
    window.sessionStorage.setItem(SEARCH_TRACKING_SESSION_KEY, sessionId);
    return sessionId;
  } catch {
    return createSearchTrackingUuid();
  }
}

function getSearchTrackingPageContext() {
  const pageContext = getSearchTrackingConfig().pageContext || {};

  return {
    type: sanitizeSearchTrackingString(pageContext.type).toLowerCase() || "other",
    handle: sanitizeSearchTrackingString(pageContext.handle),
    gender: normalizeSearchTrackingGender(pageContext.gender),
    collectionId: sanitizeSearchTrackingString(pageContext.collectionId),
  };
}

function readSearchTrackingEntryContext() {
  try {
    const rawValue = window.sessionStorage.getItem(SEARCH_TRACKING_ENTRY_KEY);
    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue?.query || !parsedValue?.ts) return null;

    if (Date.now() - parsedValue.ts > SEARCH_TRACKING_ENTRY_TTL_MS) {
      window.sessionStorage.removeItem(SEARCH_TRACKING_ENTRY_KEY);
      return null;
    }

    return parsedValue;
  } catch {
    return null;
  }
}

function resolveSearchTrackingContext(query = "", contextOverride = {}, allowStoredEntry = true) {
  const overrideContext = {
    type: sanitizeSearchTrackingString(contextOverride.type).toLowerCase(),
    handle: sanitizeSearchTrackingString(contextOverride.handle),
    gender: normalizeSearchTrackingGender(contextOverride.gender),
    collectionId: sanitizeSearchTrackingString(contextOverride.collectionId),
  };

  if (
    overrideContext.type ||
    overrideContext.handle ||
    overrideContext.gender ||
    overrideContext.collectionId
  ) {
    return {
      type: overrideContext.type || "other",
      handle: overrideContext.handle,
      gender: overrideContext.gender,
      collectionId: overrideContext.collectionId,
    };
  }

  const pageContext = getSearchTrackingPageContext();
  const normalizedQuery = normalizeSearchTrackingValue(query);

  if (allowStoredEntry && pageContext.type === "search" && normalizedQuery) {
    const storedEntry = readSearchTrackingEntryContext();

    if (storedEntry?.query === normalizedQuery) {
      return {
        type: sanitizeSearchTrackingString(storedEntry.contextType).toLowerCase() || "search",
        handle: sanitizeSearchTrackingString(storedEntry.contextHandle),
        gender: normalizeSearchTrackingGender(storedEntry.contextGender),
        collectionId: sanitizeSearchTrackingString(storedEntry.collectionId),
      };
    }
  }

  return pageContext;
}

function rememberSearchTrackingEntryContext(query, contextOverride = {}) {
  const normalizedQuery = normalizeSearchTrackingValue(query);
  if (!normalizedQuery) return;

  const resolvedContext = resolveSearchTrackingContext(normalizedQuery, contextOverride, false);

  try {
    window.sessionStorage.setItem(
      SEARCH_TRACKING_ENTRY_KEY,
      JSON.stringify({
        query: normalizedQuery,
        ts: Date.now(),
        contextType: resolvedContext.type,
        contextHandle: resolvedContext.handle,
        contextGender: resolvedContext.gender,
        collectionId: resolvedContext.collectionId,
      }),
    );
  } catch {
    // Session storage is optional for tracking context carry-over.
  }
}

function isSearchTrackingEnabled() {
  const config = getSearchTrackingConfig();
  return Boolean(
    sanitizeSearchTrackingString(config.apiBaseUrl || config.appBaseUrl) &&
      sanitizeSearchTrackingString(config.shop),
  );
}

function getSearchTrackingUrl() {
  const appBaseUrl = sanitizeSearchTrackingString(
    getSearchTrackingConfig().apiBaseUrl || getSearchTrackingConfig().appBaseUrl,
  );
  if (!appBaseUrl) return "";

  return `${appBaseUrl.replace(/\/+$/, "")}/api/search/track`;
}

function getSearchTrackingEntryData(rawEvent = {}) {
  const explicitQuery = normalizeSearchTrackingValue(rawEvent.query);
  const storedEntry =
    explicitQuery || rawEvent.useStoredEntry === false ? null : readSearchTrackingEntryContext();
  const query = explicitQuery || storedEntry?.query || "";
  const mergedContext =
    storedEntry && !rawEvent.context
      ? {
          type: storedEntry.contextType,
          handle: storedEntry.contextHandle,
          gender: storedEntry.contextGender,
          collectionId: storedEntry.collectionId,
        }
      : rawEvent.context || {};

  return {
    query,
    context: mergedContext,
    storedEntry,
  };
}

function readSearchTrackingStorage(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key) || "{}");
  } catch {
    return {};
  }
}

function writeSearchTrackingStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Local storage is optional for attribution persistence.
  }
}

function readAttributedSearchProducts() {
  const storedProducts = readSearchTrackingStorage(SEARCH_TRACKING_ATTRIBUTED_PRODUCTS_KEY);
  const now = Date.now();

  Object.keys(storedProducts).forEach((productId) => {
    const entry = storedProducts[productId];
    if (!entry?.ts || now - entry.ts > SEARCH_TRACKING_ATTRIBUTION_TTL_MS) {
      delete storedProducts[productId];
    }
  });

  return storedProducts;
}

function writeAttributedSearchProducts(products) {
  writeSearchTrackingStorage(SEARCH_TRACKING_ATTRIBUTED_PRODUCTS_KEY, products);
}

function buildAttributedSearchProductContext(attributedProduct = {}) {
  return {
    type: sanitizeSearchTrackingString(attributedProduct.contextType).toLowerCase(),
    handle: sanitizeSearchTrackingString(attributedProduct.contextHandle),
    gender: normalizeSearchTrackingGender(attributedProduct.contextGender),
    collectionId: sanitizeSearchTrackingString(attributedProduct.collectionId),
  };
}

function readAttributedSearchProduct(productId) {
  const normalizedProductId = normalizeSearchTrackingProductId(productId);
  if (!normalizedProductId) return null;

  const attributedProducts = readAttributedSearchProducts();
  return attributedProducts[normalizedProductId] || null;
}

function rememberAttributedSearchProduct(productId, rawEvent = {}) {
  const normalizedProductId = normalizeSearchTrackingProductId(productId);
  if (!normalizedProductId) return;

  const entryData = getSearchTrackingEntryData(rawEvent);
  if (!entryData.query) return;

  const resolvedContext = resolveSearchTrackingContext(entryData.query, entryData.context, false);
  const products = readAttributedSearchProducts();

  products[normalizedProductId] = {
    query: entryData.query,
    ts: Date.now(),
    surface: sanitizeSearchTrackingString(rawEvent.surface),
    marketId: normalizeSearchTrackingMarketId(
      rawEvent.marketId || getSearchTrackingConfig().marketId,
    ),
    contextType: resolvedContext.type,
    contextHandle: resolvedContext.handle,
    contextGender: resolvedContext.gender,
    collectionId: resolvedContext.collectionId,
  };

  writeAttributedSearchProducts(products);
}

function readTrackedPurchaseMap() {
  return readSearchTrackingStorage(SEARCH_TRACKING_PURCHASED_ORDERS_KEY);
}

function writeTrackedPurchaseMap(purchaseMap) {
  writeSearchTrackingStorage(SEARCH_TRACKING_PURCHASED_ORDERS_KEY, purchaseMap);
}

function markTrackedPurchase(orderId, productId) {
  const normalizedOrderId = sanitizeSearchTrackingString(orderId);
  const normalizedProductId = normalizeSearchTrackingProductId(productId);
  if (!normalizedOrderId || !normalizedProductId) return;

  const purchaseMap = readTrackedPurchaseMap();
  const trackedProducts = Array.isArray(purchaseMap[normalizedOrderId])
    ? purchaseMap[normalizedOrderId]
    : [];

  if (!trackedProducts.includes(normalizedProductId)) {
    trackedProducts.push(normalizedProductId);
    purchaseMap[normalizedOrderId] = trackedProducts;
    writeTrackedPurchaseMap(purchaseMap);
  }
}

function hasTrackedPurchase(orderId, productId) {
  const normalizedOrderId = sanitizeSearchTrackingString(orderId);
  const normalizedProductId = normalizeSearchTrackingProductId(productId);
  if (!normalizedOrderId || !normalizedProductId) return false;

  const purchaseMap = readTrackedPurchaseMap();
  return Array.isArray(purchaseMap[normalizedOrderId])
    ? purchaseMap[normalizedOrderId].includes(normalizedProductId)
    : false;
}

function clearAttributedSearchProducts(productIds = []) {
  if (!productIds.length) return;

  const products = readAttributedSearchProducts();
  productIds.forEach((productId) => {
    delete products[normalizeSearchTrackingProductId(productId)];
  });
  writeAttributedSearchProducts(products);
}

function buildSearchTrackingEvent(rawEvent = {}) {
  const entryData = getSearchTrackingEntryData(rawEvent);
  const normalizedQuery = entryData.query;
  const resolvedContext = resolveSearchTrackingContext(normalizedQuery, entryData.context || {});
  const marketId = normalizeSearchTrackingMarketId(
    rawEvent.marketId || getSearchTrackingConfig().marketId,
  );
  const event = {
    eventType: sanitizeSearchTrackingString(rawEvent.eventType),
  };

  if (normalizedQuery) event.query = normalizedQuery;
  if (rawEvent.surface) event.surface = sanitizeSearchTrackingString(rawEvent.surface);
  if (marketId) event.marketId = marketId;
  if (resolvedContext.type) event.contextType = resolvedContext.type;
  if (resolvedContext.handle) event.contextHandle = resolvedContext.handle;
  if (resolvedContext.gender) event.contextGender = resolvedContext.gender;

  const suggestionLabel = sanitizeSearchTrackingString(rawEvent.suggestionLabel);
  if (suggestionLabel) event.suggestionLabel = suggestionLabel;

  const filterValue = sanitizeSearchTrackingString(rawEvent.filterValue);
  if (filterValue) event.filterValue = filterValue;

  const collectionId =
    sanitizeSearchTrackingString(rawEvent.collectionId) ||
    (resolvedContext.type === "collection" ? resolvedContext.collectionId : "");
  if (collectionId) event.collectionId = collectionId;

  const productId = normalizeSearchTrackingProductId(rawEvent.productId);
  if (productId) event.productId = productId;

  const orderId = sanitizeSearchTrackingString(rawEvent.orderId);
  if (orderId) event.orderId = orderId;

  const position = sanitizeSearchTrackingInteger(rawEvent.position);
  if (position !== null) event.position = position;

  const resultCount = sanitizeSearchTrackingInteger(rawEvent.resultCount);
  if (resultCount !== null) event.resultCount = resultCount;

  const revenue = sanitizeSearchTrackingNumber(rawEvent.revenue);
  if (revenue !== null) event.revenue = revenue;

  return event;
}

function buildSearchTrackingEventKey(event = {}) {
  return JSON.stringify([
    sanitizeSearchTrackingString(event.eventType),
    sanitizeSearchTrackingString(event.query),
    sanitizeSearchTrackingString(event.surface),
    sanitizeSearchTrackingString(event.contextType),
    sanitizeSearchTrackingString(event.contextHandle),
    sanitizeSearchTrackingString(event.contextGender),
    sanitizeSearchTrackingString(event.collectionId),
    sanitizeSearchTrackingString(event.productId),
    sanitizeSearchTrackingString(event.suggestionLabel),
    sanitizeSearchTrackingString(event.filterValue),
    sanitizeSearchTrackingString(event.orderId),
    event.position ?? "",
    event.resultCount ?? "",
    event.revenue ?? "",
  ]);
}

function shouldSkipDuplicateSearchTrackingEvent(event = {}) {
  const eventKey = buildSearchTrackingEventKey(event);
  if (!eventKey) return false;

  const now = Date.now();

  recentSearchTrackingEvents.forEach((timestamp, key) => {
    if (now - timestamp > SEARCH_TRACKING_DEDUPE_WINDOW_MS) {
      recentSearchTrackingEvents.delete(key);
    }
  });

  const previousTimestamp = recentSearchTrackingEvents.get(eventKey);
  if (typeof previousTimestamp === "number" && now - previousTimestamp <= SEARCH_TRACKING_DEDUPE_WINDOW_MS) {
    return true;
  }

  recentSearchTrackingEvents.set(eventKey, now);
  return false;
}

function sendSearchTrackingPayload(payload, { transport = "fetch", keepalive = false } = {}) {
  const trackingUrl = getSearchTrackingUrl();
  if (!trackingUrl) return Promise.resolve(false);

  const body = JSON.stringify(payload);

  if (transport === "beacon" && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      const sent = navigator.sendBeacon(trackingUrl, blob);
      if (sent) return Promise.resolve(true);
    } catch {
      // Fall back to fetch below.
    }
  }

  return fetch(trackingUrl, {
    method: "POST",
    mode: "cors",
    credentials: "omit",
    keepalive: Boolean(keepalive || transport === "beacon"),
    headers: {
      "Content-Type": "application/json",
    },
    body,
  })
    .then((response) => response.ok)
    .catch(() => false);
}

function sendSearchTrackingEvent(event, options = {}) {
  const preferNativeTransport =
    options.transport === "beacon" ||
    Boolean(options.keepalive) ||
    Boolean(options.preferNativeTransport);
  const searchClient = preferNativeTransport ? null : getSearchTrackingClient();
  if (searchClient?.track) {
    return searchClient
      .track(event)
      .then(() => true)
      .catch(() => false);
  }

  const shop = sanitizeSearchTrackingString(getSearchTrackingConfig().shop);
  const payload = {
    shop,
    visitorId: getSearchTrackingVisitorId(),
    sessionId: getSearchTrackingSessionId(),
    events: [event],
  };

  return sendSearchTrackingPayload(payload, options);
}

function trackSearchEvent(rawEvent = {}, options = {}) {
  if (!isSearchTrackingEnabled()) {
    return Promise.resolve(false);
  }

  const eventType = sanitizeSearchTrackingString(rawEvent.eventType);
  if (!eventType) {
    return Promise.resolve(false);
  }

  const event = buildSearchTrackingEvent(rawEvent);
  if (shouldSkipDuplicateSearchTrackingEvent(event)) {
    return Promise.resolve(false);
  }

  const entryData = getSearchTrackingEntryData(rawEvent);

  if (
    rawEvent.rememberEntry !== false &&
    (eventType === "SEARCH_SUBMITTED" || eventType === "PREDICTIVE_CLICK")
  ) {
    rememberSearchTrackingEntryContext(entryData.query, entryData.context || {});
  }

  if (
    rawEvent.rememberAttribution !== false &&
    (eventType === "PREDICTIVE_CLICK" || eventType === "RESULT_CLICK") &&
    event.productId &&
    entryData.query
  ) {
    rememberAttributedSearchProduct(event.productId, {
      ...rawEvent,
      query: entryData.query,
      context: entryData.context,
      surface: event.surface || rawEvent.surface,
      marketId: event.marketId || rawEvent.marketId,
    });
  }

  return sendSearchTrackingEvent(event, options);
}

function getSearchTrackingRoutePath() {
  try {
    return new URL(window.routes?.search_url || "/search", window.location.origin).pathname;
  } catch {
    return "/search";
  }
}

function isSearchTrackingForm(form) {
  if (!(form instanceof HTMLFormElement)) return false;
  if (!form.querySelector('input[name="q"]')) return false;

  try {
    const actionPath = new URL(form.getAttribute("action") || window.location.pathname, window.location.origin)
      .pathname;
    return actionPath === getSearchTrackingRoutePath();
  } catch {
    return false;
  }
}

function getSearchTrackingFormQuery(form) {
  if (!(form instanceof HTMLFormElement)) return "";

  const queryInput = form.querySelector('input[name="q"]');
  return normalizeSearchTrackingValue(queryInput?.value);
}

function getSearchTrackingPageQuery() {
  const bannerQuery = normalizeSearchTrackingValue(
    document.querySelector("[data-search-page-term]")?.getAttribute("data-search-page-term"),
  );
  if (bannerQuery) return bannerQuery;

  try {
    return normalizeSearchTrackingValue(new URLSearchParams(window.location.search).get("q"));
  } catch {
    return "";
  }
}

function getSearchTrackingResolvedResultsCount() {
  const searchBanner = document.querySelector("[data-search-results-resolved-count]");
  const bannerCount = sanitizeSearchTrackingInteger(
    searchBanner?.getAttribute("data-search-results-resolved-count"),
  );
  if (bannerCount !== null) return bannerCount;

  const productCards = document.querySelectorAll("#product-grid .product-card");
  return productCards.length || 0;
}

function autoTrackSearchSubmitted(event) {
  const form = event.target;
  if (!isSearchTrackingForm(form)) return;

  const query = getSearchTrackingFormQuery(form);
  if (!query) return;

  window.UnboundSearchTracking?.trackSearchSubmitted?.(
    {
      query,
      surface: "RESULTS",
    },
    { transport: "beacon" },
  );
}

function autoTrackPredictiveClick(searchLink) {
  if (!(searchLink instanceof Element)) return false;

  const query = normalizeSearchTrackingValue(
    searchLink.getAttribute("data-search-matched-query") || searchLink.getAttribute("data-search-query"),
  );
  if (!query) return false;

  const itemType = sanitizeSearchTrackingString(searchLink.getAttribute("data-search-item-type")) || "query";
  const position = sanitizeSearchTrackingInteger(searchLink.getAttribute("data-search-position"));
  const resultCount = sanitizeSearchTrackingInteger(searchLink.getAttribute("data-search-result-count"));
  const productId = searchLink.getAttribute("data-search-product-id") || "";
  const suggestionLabel =
    itemType === "product"
      ? ""
      : normalizeSearchTrackingValue(
          searchLink.getAttribute("data-search-suggestion-label") ||
            searchLink.getAttribute("data-search-query"),
        );

  const isPredictiveSuggestion =
    Boolean(searchLink.closest("[data-search-suggestions]")) ||
    Boolean(searchLink.closest("[data-typing-state]")) ||
    position !== null ||
    Boolean(productId);

  if (isPredictiveSuggestion) {
    window.UnboundSearchTracking?.trackPredictiveClick?.(
      {
        query,
        productId,
        suggestionLabel,
        position,
        resultCount,
      },
      { transport: "beacon" },
    );
  }

  if (itemType === "query") {
    window.UnboundSearchTracking?.trackSearchSubmitted?.(
      {
        query,
        surface: "RESULTS",
      },
      { transport: "beacon" },
    );
  }

  return isPredictiveSuggestion || itemType === "query";
}

function autoTrackResultClick(resultLink) {
  if (!(resultLink instanceof Element)) return false;

  const productCard = resultLink.closest(".product-card");
  const resultsContainer = resultLink.closest("#product-grid");
  const query = getSearchTrackingPageQuery();

  if (!productCard || !resultsContainer || !query) {
    return false;
  }

  const productCards = Array.from(resultsContainer.querySelectorAll(".product-card"));
  const position = productCards.indexOf(productCard) + 1;

  window.UnboundSearchTracking?.trackResultClick?.(
    {
      query,
      productId: productCard.getAttribute("data-product-id") || "",
      position: position > 0 ? position : null,
      resultCount: getSearchTrackingResolvedResultsCount(),
    },
    { transport: "beacon" },
  );

  return true;
}

function autoTrackSearchClick(event) {
  if (event?.__unboundSearchTrackingHandled) return;

  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const predictiveLink = target.closest("[data-search-query]");
  if (predictiveLink && autoTrackPredictiveClick(predictiveLink)) {
    return;
  }

  const resultLink = target.closest(".main-search .product-card__link");
  if (resultLink) {
    autoTrackResultClick(resultLink);
  }
}

function initSearchTrackingAutowire() {
  if (window.__unboundSearchTrackingAutowireInitialized) return;
  window.__unboundSearchTrackingAutowireInitialized = true;

  document.addEventListener("submit", autoTrackSearchSubmitted);
  document.addEventListener("click", autoTrackSearchClick);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSearchTrackingAutowire, { once: true });
} else {
  initSearchTrackingAutowire();
}

function trackAttributedAddToCart(options = {}, transportOptions = {}) {
  const normalizedProductId = normalizeSearchTrackingProductId(options.productId);
  const attributedProduct = readAttributedSearchProduct(normalizedProductId);
  const entryData = attributedProduct?.query
    ? {
        query: attributedProduct.query,
        context: buildAttributedSearchProductContext(attributedProduct),
      }
    : getSearchTrackingEntryData(options);

  if (!normalizedProductId || !entryData.query) {
    return Promise.resolve(false);
  }

  const surface =
    sanitizeSearchTrackingString(options.surface) ||
    sanitizeSearchTrackingString(attributedProduct?.surface) ||
    "RESULTS";
  const marketId =
    normalizeSearchTrackingMarketId(options.marketId) ||
    normalizeSearchTrackingMarketId(attributedProduct?.marketId);

  rememberAttributedSearchProduct(normalizedProductId, {
    ...options,
    query: entryData.query,
    context: entryData.context,
    surface,
    marketId,
  });

  return trackSearchEvent(
    {
      ...options,
      productId: normalizedProductId,
      query: entryData.query,
      context: entryData.context,
      marketId,
      eventType: "ADD_TO_CART",
      surface,
    },
    transportOptions,
  );
}

function trackAttributedPurchase(options = {}, transportOptions = {}) {
  const lineItems = Array.isArray(options.lineItems) ? options.lineItems : [];
  const orderId = sanitizeSearchTrackingString(options.orderId);
  if (!lineItems.length || !orderId) {
    return Promise.resolve(false);
  }

  const attributedProducts = readAttributedSearchProducts();
  const purchasePromises = [];
  const trackedProductIds = [];

  lineItems.forEach((lineItem) => {
    const normalizedProductId = normalizeSearchTrackingProductId(lineItem.productId);
    if (!normalizedProductId || hasTrackedPurchase(orderId, normalizedProductId)) {
      return;
    }

    const attribution = attributedProducts[normalizedProductId];
    if (!attribution?.query) {
      return;
    }

    purchasePromises.push(
      trackSearchEvent(
        {
          eventType: "PURCHASE",
          surface:
            sanitizeSearchTrackingString(options.surface) ||
            sanitizeSearchTrackingString(attribution.surface) ||
            "RESULTS",
          query: attribution.query,
          productId: normalizedProductId,
          orderId,
          revenue: lineItem.revenue ?? options.revenue,
          marketId: attribution.marketId,
          context: {
            type: attribution.contextType,
            handle: attribution.contextHandle,
            gender: attribution.contextGender,
            collectionId: attribution.collectionId,
          },
          useStoredEntry: false,
          rememberEntry: false,
        },
        transportOptions,
      ).then((didTrack) => {
        if (didTrack) {
          markTrackedPurchase(orderId, normalizedProductId);
          trackedProductIds.push(normalizedProductId);
        }

        return didTrack;
      }),
    );
  });

  if (!purchasePromises.length) {
    return Promise.resolve(false);
  }

  return Promise.all(purchasePromises).then((results) => {
    if (trackedProductIds.length) {
      clearAttributedSearchProducts(trackedProductIds);
    }

    return results.some(Boolean);
  });
}

window.UnboundSearchTracking = {
  isEnabled: isSearchTrackingEnabled,
  normalizeSearchValue: normalizeSearchTrackingValue,
  getPageContext: getSearchTrackingPageContext,
  resolveContext: resolveSearchTrackingContext,
  getRememberedEntry: readSearchTrackingEntryContext,
  rememberSearchEntry: rememberSearchTrackingEntryContext,
  trackEvent: trackSearchEvent,
  trackSearchSubmitted(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "SEARCH_SUBMITTED",
      },
      transportOptions,
    );
  },
  trackPredictiveImpression(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "PREDICTIVE_IMPRESSION",
        surface: "PREDICTIVE",
        rememberEntry: false,
      },
      transportOptions,
    );
  },
  trackPredictiveClick(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "PREDICTIVE_CLICK",
        surface: "PREDICTIVE",
      },
      transportOptions,
    );
  },
  trackZeroResults(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "ZERO_RESULTS",
        rememberEntry: false,
      },
      transportOptions,
    );
  },
  trackResultsImpression(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "RESULTS_IMPRESSION",
        surface: "RESULTS",
        rememberEntry: false,
      },
      transportOptions,
    );
  },
  trackResultClick(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "RESULT_CLICK",
        surface: "RESULTS",
      },
      transportOptions,
    );
  },
  trackExit(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "EXIT",
        rememberEntry: false,
      },
      transportOptions,
    );
  },
  trackAddToCart(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "ADD_TO_CART",
      },
      transportOptions,
    );
  },
  trackPurchase(options = {}, transportOptions = {}) {
    return trackSearchEvent(
      {
        ...options,
        eventType: "PURCHASE",
      },
      transportOptions,
    );
  },
  trackAttributedAddToCart,
  trackAttributedPurchase,
};
